import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, ExternalLink, Loader2, Sparkles, TriangleAlert } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { KbStyleInjector } from "@/lib/kbSettings";
import { toast } from "sonner";

const TYPES = [
  ["danger", "Danger"], ["warning", "Warning"], ["important", "Important"],
  ["prerequisite", "Prerequisite"], ["note", "Note"], ["tip", "Tip"],
  ["best-practice", "Best practice"], ["dont", "Don't"], ["do", "Do"],
  ["success", "Success"], ["example", "Example"],
];

/** Danger downgrade pass: 91 red boxes, most of them not dangerous. Claude
 *  proposes the right house type, the editor confirms one click at a time. */
export default function KBDangerReview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [override, setOverride] = useState({});
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/kb/callouts/danger-review?batch=10");
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load the review");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const suggest = async () => {
    try {
      const { data: r } = await api.post("/kb/callouts/danger-suggest");
      toast.info(r.started ? `Claude is reading ${r.pending} boxes — refresh in a minute`
        : r.running ? "Claude is already working through them" : "Every box already has a suggestion");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the AI pass");
    }
  };

  const applySafe = async () => {
    setApplying(true);
    try {
      const { data: r } = await api.post("/kb/callouts/apply-suggestions",
        { types: ["note", "prerequisite"] });
      toast.success(r.applied
        ? `${r.applied} boxes retyped — ${r.left_for_you} judgement calls left for you`
        : "Nothing safe left to apply");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply the safe suggestions");
    } finally { setApplying(false); }
  };

  const decide = async (row, variant) => {
    setBusy(row.id);
    try {
      if (variant === "danger") {
        await api.post("/kb/callouts/keep", { slug: row.slug, index: row.index, queue: "danger" });
      } else {
        await api.post("/kb/callouts/promote", { slug: row.slug, index: row.index, variant });
      }
      setData((d) => ({
        ...d,
        batch: d.batch.filter((b) => b.id !== row.id),
        pending: Math.max(0, d.pending - 1),
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply that");
    } finally { setBusy(""); }
  };

  useEffect(() => {
    if (data && !data.batch.length && data.pending > 0 && !loading) load();
  }, [data, loading, load]);

  const reviewed = data ? data.total - data.pending : 0;
  const pct = data && data.total ? Math.round((reviewed / data.total) * 100) : 0;

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-6" data-testid="kb-danger-review-page">
      <KbStyleInjector />
      <Link to="/kb/callout-audit" className="inline-flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 dark:text-slate-400 dark:hover:text-slate-100">
        <ArrowLeft className="h-3.5 w-3.5" /> Callout audit
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900 dark:text-slate-100 dark:text-slate-50">
            <TriangleAlert className="h-6 w-6" /> Danger downgrade
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Red should mean red. Claude proposes the house type for each Danger box against your
            style rules — confirm it, pick a different one, or keep it as Danger.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={suggest} data-testid="kb-danger-suggest">
            <Sparkles className="mr-1 h-4 w-4" /> Get AI suggestions
          </Button>
          <Button variant="outline" onClick={applySafe} disabled={applying}
            data-testid="kb-danger-apply-safe"
            title="Applies only the Note and Prerequisite suggestions — Warning, Important and Danger stay for you">
            {applying ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            Apply the safe ones
          </Button>
        </div>
      </div>

      <Card className="p-4" data-testid="kb-danger-progress">
        <div className="flex flex-wrap items-baseline gap-3 text-sm">
          <span className="font-semibold text-slate-800 dark:text-slate-100">
            {data ? `${reviewed} of ${data.total} reviewed` : "…"}
          </span>
          <span className="text-slate-500 dark:text-slate-400">
            {data ? `${data.pending} left${data.unsuggested ? ` · ${data.unsuggested} without a suggestion` : ""}` : ""}
          </span>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400 dark:text-slate-500" />}
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
      </Card>

      {data && !data.pending && (
        <Card className="p-8 text-center" data-testid="kb-danger-done">
          <Check className="mx-auto h-8 w-8 text-emerald-600" />
          <p className="mt-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
            Every Danger box has been reviewed.
          </p>
        </Card>
      )}

      {(data?.batch || []).map((row) => {
        const picked = override[row.id] || row.suggested || "note";
        return (
          <Card key={row.id} className="p-4" data-testid={`kb-danger-card-${row.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                <span className="h-2.5 w-2.5 rounded-full kb-swatch-danger" /> Danger
              </span>
              {row.title && <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{row.title}</span>}
              {row.suggested && (
                <span className="inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary"
                  data-testid={`kb-danger-suggestion-${row.id}`}>
                  <Sparkles className="h-3 w-3" /> suggests {row.suggested_label}
                  {row.why ? ` — ${row.why}` : ""}
                </span>
              )}
              <Link to={`/kb/a/${row.slug}`} target="_blank" rel="noreferrer"
                className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-primary">
                <ExternalLink className="h-3.5 w-3.5" /> {row.article}
              </Link>
            </div>
            <p className="mt-2 line-clamp-6 text-sm text-slate-600 dark:text-slate-300">{row.text}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button size="sm" disabled={busy === row.id} onClick={() => decide(row, picked)}
                data-testid={`kb-danger-apply-${row.id}`}>
                {busy === row.id ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                Make it {TYPES.find(([v]) => v === picked)?.[1] || picked}
              </Button>
              <Select value={picked} onValueChange={(v) => setOverride((o) => ({ ...o, [row.id]: v }))}>
                <SelectTrigger className="h-8 w-44 text-xs" data-testid={`kb-danger-type-${row.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="ghost" disabled={busy === row.id}
                onClick={() => decide(row, "danger")} data-testid={`kb-danger-keep-${row.id}`}>
                Keep as Danger
              </Button>
              <span className="text-[11px] text-slate-400 dark:text-slate-500">
                {row.category}{row.risky_wording ? " · risky wording found" : ""}
              </span>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
